
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Brain, Palette, Zap } from "lucide-react";
import { EnhancedColorSuggestion } from "@/hooks/color-check/types";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";

interface ColorCheckFormProps {
  searchTerm: string;
  suggestions: EnhancedColorSuggestion[];
  showSuggestions: boolean;
  isLoading: boolean;
  recentChecks: any[];
  formatTimestamp: (date?: Date) => string;
  onSearchChange: (term: string) => void;
  onSelectSuggestion: (suggestion: EnhancedColorSuggestion) => void;
  onCheckColor: () => void;
}

const ColorCheckForm = ({
  searchTerm,
  suggestions,
  showSuggestions,
  isLoading,
  recentChecks,
  formatTimestamp,
  onSearchChange,
  onSelectSuggestion,
  onCheckColor
}: ColorCheckFormProps) => {
  // Fixed handler to call analyzeColor with no parameters (uses searchTerm)
  const handleAnalyzeClick = () => {
    console.log("🔍 Analyze button clicked with searchTerm:", searchTerm);
    onCheckColor(); // This calls analyzeColor() without parameters
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Brain className="mr-2 h-5 w-5 text-blue-500" />
          AI Kleuranalyse
        </CardTitle>
        <CardDescription>
          Geavanceerde AI-gebaseerde kleurherkenning met self-learning technologie
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2 relative">
            <Label htmlFor="searchTerm">Kleurcode + Product</Label>
            <Input
              id="searchTerm"
              placeholder="Bijv. S2U Nova Satin 1 LTR - 129 - Shirting - Little Greene"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoComplete="off"
            />
            
            {showSuggestions && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => onSelectSuggestion(suggestion)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{suggestion.label}</span>
                      <div className="flex items-center gap-2">
                        {suggestion.baseColor && (
                          <Badge variant="outline" className="text-xs">
                            {suggestion.baseColor}
                          </Badge>
                        )}
                        {suggestion.confidence && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            {Math.round(suggestion.confidence * 100)}%
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <Button 
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            onClick={handleAnalyzeClick}
            disabled={isLoading || !searchTerm.trim()}
          >
            {isLoading ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                AI Analyseren...
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Start AI Analyse
              </>
            )}
          </Button>

          {/* AI Features Info */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-sm text-blue-800">
              <div className="font-medium mb-1">🧠 AI Features:</div>
              <ul className="text-xs space-y-1 text-blue-700">
                <li>• Intelligente productherkenning</li>
                <li>• Automatische basis kleur voorspelling</li>
                <li>• Real-time voorraad matching</li>
                <li>• Self-learning van feedback</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>

      {recentChecks.length > 0 && (
        <>
          <Separator />
          <CardHeader className="pt-4 pb-2">
            <CardTitle className="text-sm flex items-center">
              <Clock className="mr-2 h-4 w-4" />
              Recente AI Analyses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {recentChecks.map((check, index) => (
                <div key={index} className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ 
                          backgroundColor: check.parsedInfo?.colorCode?.startsWith("RAL") ? 
                            `var(--${check.parsedInfo.colorCode.toLowerCase()}, #ccc)` : "#ccc" 
                        }}
                      />
                      <span className="text-sm font-medium">
                        {check.parsedInfo?.colorCode || (check.originalInput ? check.originalInput.substring(0, 20) : 'Onbekende kleur')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-muted-foreground">
                        {check.parsedInfo?.productName || 'Onbekend product'} - {formatTimestamp(check.timestamp)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                      {check.basePrediction?.baseColor || 'Onbekend'}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {Math.round((check.basePrediction?.confidence || 0) * 100)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default ColorCheckForm;
